import {
  useGoogleLogin,
  useGoogleOAuth,
  type NonOAuthError,
  type TokenResponse,
} from '@react-oauth/google';
import { logger } from '@sentry/react';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

const GOOGLE_SIGN_IN_STUCK_MESSAGE =
  'Google sign-in did not start properly. Please retry after disabling popup blockers or privacy extensions.';
const SHOULD_USE_MSW_AUTH = import.meta.env.VITE_ENABLE_MSW === 'true';

type PendingGoogleSignIn = {
  reject: (reason?: unknown) => void;
  resolve: (value: string) => void;
  timeoutId: number;
};

type UseSignInWithGoogleOptions = {
  timeoutMs: number;
};

export function useSignInWithGoogle({ timeoutMs }: UseSignInWithGoogleOptions): {
  signInWithGoogle: () => Promise<string>;
} {
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const pendingSignInRef = useRef<PendingGoogleSignIn | null>(null);

  const clearPendingSignIn = useCallback(() => {
    const pendingSignIn = pendingSignInRef.current;
    if (!pendingSignIn) {
      return;
    }

    window.clearTimeout(pendingSignIn.timeoutId);
    pendingSignInRef.current = null;
  }, []);

  const rejectPendingSignIn = useCallback(
    (error: unknown) => {
      const pendingSignIn = pendingSignInRef.current;

      clearPendingSignIn();
      pendingSignIn?.reject(error);
    },
    [clearPendingSignIn],
  );

  const googleLogin = useGoogleLogin({
    onError: (errorResponse) => {
      logger.error('Google OAuth error response', {
        extra: errorResponse,
      });
      rejectPendingSignIn(new Error('Google OAuth error response'));
    },
    onNonOAuthError: (error: NonOAuthError) => {
      logger.error('Google non-OAuth error', {
        extra: error,
      });

      toast.error(
        error.type === 'popup_closed'
          ? 'Google sign-in popup was closed before login completed.'
          : GOOGLE_SIGN_IN_STUCK_MESSAGE,
      );

      rejectPendingSignIn(error);
    },
    onSuccess: ({ access_token }: TokenResponse) => {
      const pendingSignIn = pendingSignInRef.current;

      clearPendingSignIn();
      pendingSignIn?.resolve(access_token);
    },
  });

  const signInWithGoogle = useCallback(() => {
    if (SHOULD_USE_MSW_AUTH) {
      return Promise.resolve('mock-google-oauth-token');
    }

    if (!scriptLoadedSuccessfully) {
      const error = new Error('Google sign-in attempted before GSI script finished loading');

      logger.error(error.message, {
        extra: {
          googleScriptLoadedSuccessfully: scriptLoadedSuccessfully,
        },
      });
      toast.error(GOOGLE_SIGN_IN_STUCK_MESSAGE);

      return Promise.reject(error);
    }

    if (pendingSignInRef.current) {
      return Promise.reject(new Error('Google sign-in is already in progress'));
    }

    return new Promise<string>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        logger.error('google sign-in timed out before completion', {
          extra: {
            googleScriptLoadedSuccessfully: scriptLoadedSuccessfully,
            vendor: 'google',
          },
        });
        alert(GOOGLE_SIGN_IN_STUCK_MESSAGE);

        rejectPendingSignIn(new Error('Google sign-in timed out before completion'));
      }, timeoutMs);

      pendingSignInRef.current = {
        reject,
        resolve,
        timeoutId,
      };

      try {
        googleLogin();
      } catch (error) {
        rejectPendingSignIn(error);
      }
    });
  }, [googleLogin, rejectPendingSignIn, scriptLoadedSuccessfully, timeoutMs]);

  useEffect(() => {
    return () => {
      clearPendingSignIn();
    };
  }, [clearPendingSignIn]);

  return {
    signInWithGoogle,
  };
}
